import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Search, Plus, Eye, Edit, Trash2, Map, MapPin, X, Loader, Utensils, Navigation, CopyPlus } from 'lucide-react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import api from '../../utils/api';
import { getDishDisplayName } from '../../utils/dishDisplay';
import './MosqueList.scss';

const KERALA_DISTRICTS = [
    'Alappuzha', 'Ernakulam', 'Idukki', 'Kannur', 'Kasaragod', 'Kollam',
    'Kottayam', 'Kozhikode', 'Malappuram', 'Palakkad', 'Pathanamthitta',
    'Thiruvananthapuram', 'Thrissur', 'Wayanad'
];

const DISH_OPTIONS = ['Biriyani', 'Neychoru', 'Pathiri', 'Snacks', 'Jeeraka Kanji ജീരക കഞ്ഞി', 'Other'];

const MosqueList = () => {
    const { success, error, confirm } = useSnackbar();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const getFiltersFromParams = () => ({
        page: parseInt(searchParams.get('page') || '1', 10),
        limit: parseInt(searchParams.get('limit') || '20', 10),
        search: searchParams.get('search') || '',
        district: searchParams.get('district') || '',
        dish: searchParams.get('dish') || '',
        status: searchParams.get('status') || '',
        verified: searchParams.get('verified') || '',
        year: parseInt(searchParams.get('year') || '2026', 10)
    });

    const [filters, setFilters] = useState(getFiltersFromParams);
    const [mosques, setMosques] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [extractingCoordinates, setExtractingCoordinates] = useState(false);
    const [bulkProgress, setBulkProgress] = useState(null);
    const [mosquesWithoutCoords, setMosquesWithoutCoords] = useState([]);
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const [copyingMosqueId, setCopyingMosqueId] = useState(null);
    const [copyOverlayMosque, setCopyOverlayMosque] = useState(null);
    const [copyOverlayForm, setCopyOverlayForm] = useState({ name: '', dish: '', description: '' });

    const currentYear = new Date().getFullYear();
    const returnToUrl = `${location.pathname}${location.search || ''}`;

    // Sync filters from URL when user uses browser back/forward
    useEffect(() => {
        const fromUrl = getFiltersFromParams();
        setFilters(prev => {
            const same = prev.page === fromUrl.page && prev.search === fromUrl.search &&
                prev.district === fromUrl.district && prev.dish === fromUrl.dish &&
                prev.status === fromUrl.status && prev.verified === fromUrl.verified && prev.year === fromUrl.year;
            return same ? prev : fromUrl;
        });
    }, [searchParams]);

    useEffect(() => {
        fetchMosques();
        fetchMosquesWithoutCoords();
    }, [filters]);

    const updateFilters = (updates) => {
        const resetPage = 'status' in updates || 'district' in updates || 'dish' in updates || 'verified' in updates || 'search' in updates || 'year' in updates;
        const next = { ...filters, ...updates, ...(resetPage && !('page' in updates) ? { page: 1 } : {}) };
        setFilters(next);
        const params = new URLSearchParams();
        Object.entries(next).forEach(([k, v]) => {
            if (v !== '' && v !== undefined && v !== null) params.set(k, String(v));
        });
        setSearchParams(params, { replace: true });
    };

    const fetchMosquesWithoutCoords = async () => {
        try {
            const response = await api.get('/admin/mosques/without-coordinates', {
                params: { year: filters.year }
            });
            if (response.data.success) {
                setMosquesWithoutCoords(response.data.mosques);
            }
        } catch (error) {
            console.error('Error fetching mosques without coordinates:', error);
        }
    };

    const fetchMosques = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            Object.keys(filters).forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            const response = await api.get(`/admin/mosques?${params.toString()}`);
            if (response.data.success) {
                setMosques(response.data.mosques);
                setPagination(response.data.pagination);
            }
        } catch (error) {
            console.error('Error fetching mosques:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (mosqueID) => {
        confirm(
            'Are you sure you want to delete this mosque? This action cannot be undone.',
            async () => {
                try {
                    await api.delete(`/admin/mosques/${mosqueID}`);
                    success('Mosque deleted successfully');
                    fetchMosques();
                } catch (err) {
                    error(err.response?.data?.message || 'Error deleting mosque');
                }
            }
        );
    };

    const handleExtractCoordinates = async (mosqueID) => {
        try {
            setExtractingCoordinates(true);
            const response = await api.post('/admin/mosques/extract-coordinates', { mosqueID });
            if (response.data.success) {
                success(`Coordinates extracted successfully: ${response.data.coordinates.latitude.toFixed(6)}, ${response.data.coordinates.longitude.toFixed(6)}`);
                fetchMosques();
                fetchMosquesWithoutCoords();
            }
        } catch (err) {
            error(err.response?.data?.message || 'Error extracting coordinates');
        } finally {
            setExtractingCoordinates(false);
        }
    };

    const handleBulkExtractCoordinates = async () => {
        if (mosquesWithoutCoords.length === 0) {
            success('All mosques already have coordinates!');
            return;
        }

        confirm(
            `Fetch coordinates for all ${mosquesWithoutCoords.length} mosques without coordinates? This may take a few minutes.`,
            async () => {
                try {
                    setBulkProgress({
                        current: 0,
                        total: mosquesWithoutCoords.length,
                        successCount: 0,
                        errorCount: 0,
                        errors: [],
                        currentMosqueID: null
                    });

                    // Use the new endpoint that automatically finds and processes all mosques
                    const response = await api.post('/admin/mosques/fetch-all-coordinates');

                    if (response.data.success) {
                        setBulkProgress({
                            current: response.data.summary.total,
                            total: response.data.summary.total,
                            successCount: response.data.summary.successCount,
                            errorCount: response.data.summary.errorCount,
                            skippedCount: response.data.summary.skippedCount || 0,
                            errors: response.data.errors || [],
                            currentMosqueID: null
                        });
                        success(`Successfully extracted coordinates for ${response.data.summary.successCount} mosques`);
                        fetchMosques();
                        fetchMosquesWithoutCoords();
                    }
                } catch (err) {
                    error(err.response?.data?.message || 'Error fetching all coordinates');
                    setBulkProgress(null);
                }
            }
        );
    };

    const hasCoordinates = (mosque) => {
        return mosque.location?.latitude && mosque.location?.longitude;
    };

    const handleStatusChange = async (mosqueID, newStatus) => {
        setUpdatingStatusId(mosqueID);
        try {
            await api.put(`/admin/mosques/${mosqueID}`, { status: newStatus });
            success('Status updated');
            fetchMosques();
        } catch (err) {
            error(err.response?.data?.message || 'Failed to update status');
        } finally {
            setUpdatingStatusId(null);
        }
    };

    const openCopyOverlay = (mosque) => {
        setCopyOverlayMosque(mosque);
        setCopyOverlayForm({
            name: mosque.name || '',
            dish: mosque.dish || '',
            description: mosque.description || ''
        });
    };

    const closeCopyOverlay = () => {
        setCopyOverlayMosque(null);
        setCopyOverlayForm({ name: '', dish: '', description: '' });
    };

    const handleCopyForYear = async (mosqueID, overrides = {}) => {
        setCopyingMosqueId(mosqueID);
        try {
            const response = await api.post(`/admin/mosques/${mosqueID}/copy-for-year`, {
                targetYear: currentYear,
                overrides: {
                    name: overrides.name || undefined,
                    dish: overrides.dish || undefined,
                    description: overrides.description !== undefined ? overrides.description : undefined
                }
            });
            if (response.data.success) {
                success(`Mosque added to ${currentYear} list (${response.data.mosque?.mosqueID})`);
                closeCopyOverlay();
                fetchMosques();
                fetchMosquesWithoutCoords();
            }
        } catch (err) {
            error(err.response?.data?.message || 'Failed to copy mosque');
        } finally {
            setCopyingMosqueId(null);
        }
    };

    const handleCopyOverlaySubmit = () => {
        if (!copyOverlayMosque) return;
        handleCopyForYear(copyOverlayMosque.mosqueID, copyOverlayForm);
    };

    return (
        <div className="mosque-list">
            <div className="page-header">
                <h2>Mosques</h2>
                <div className="header-actions">
                    {mosquesWithoutCoords.length > 0 && (
                        <button 
                            className="extract-coords-button" 
                            onClick={handleBulkExtractCoordinates}
                            disabled={extractingCoordinates}
                            title={`Extract coordinates for ${mosquesWithoutCoords.length} mosques without coordinates`}
                        >
                            <MapPin size={18} />
                            Extract Coordinates ({mosquesWithoutCoords.length})
                        </button>
                    )}
                    <button className="map-button" onClick={() => navigate('/mosques/map')}>
                        <Map size={18} />
                        Map View
                    </button>
                    <button className="add-button" onClick={() => navigate('/mosques/add')}>
                        <Plus size={18} />
                        Add Mosque
                    </button>
                </div>
            </div>

            <div className="filters">
                <div className="search-box">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search mosques..."
                        value={filters.search}
                        onChange={(e) => updateFilters({ search: e.target.value, page: 1 })}
                    />
                </div>

                <select
                    value={filters.year}
                    onChange={(e) => updateFilters({ year: Number(e.target.value), page: 1 })}
                    title="Filter by year"
                >
                    <option value={2026}>2026</option>
                    <option value={2025}>2025</option>
                </select>

                <select
                    value={filters.district}
                    onChange={(e) => updateFilters({ district: e.target.value, page: 1 })}
                >
                    <option value="">All Districts</option>
                    {KERALA_DISTRICTS.map(d => (
                        <option key={d} value={d}>{d}</option>
                    ))}
                </select>

                <select
                    value={filters.dish}
                    onChange={(e) => updateFilters({ dish: e.target.value, page: 1 })}
                >
                    <option value="">All Dishes</option>
                    {DISH_OPTIONS.map(d => (
                        <option key={d} value={d}>{d}</option>
                    ))}
                </select>

                <select
                    value={filters.status}
                    onChange={(e) => updateFilters({ status: e.target.value, page: 1 })}
                >
                    <option value="">All Status</option>
                    <option value="available">Available</option>
                    <option value="unavailable">Unavailable</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                </select>

                <select
                    value={filters.verified}
                    onChange={(e) => updateFilters({ verified: e.target.value, page: 1 })}
                >
                    <option value="">All</option>
                    <option value="true">Verified</option>
                    <option value="false">Unverified</option>
                </select>
            </div>

            {loading ? (
                <div className="loading">Loading mosques...</div>
            ) : (
                <>
                    <div className="table-container">
                        <table className="mosques-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>District</th>
                                    <th>Dish</th>
                                    <th>Status</th>
                                    <th>Verified</th>
                                    <th>Restaurant</th>
                                    <th>Total Visits</th>
                                    <th>Direction Clicks</th>
                                    <th>Coordinates</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mosques.map((mosque) => (
                                    <tr key={mosque._id}>
                                        <td>{mosque.mosqueID}</td>
                                        <td>{mosque.name}</td>
                                        <td>{mosque.district}</td>
                                        <td>{getDishDisplayName(mosque.dish)}</td>
                                        <td>
                                            <select
                                                className={`status-select-inline status-badge ${mosque.status}`}
                                                value={mosque.status}
                                                onChange={(e) => handleStatusChange(mosque.mosqueID, e.target.value)}
                                                disabled={updatingStatusId === mosque.mosqueID}
                                                title="Update status"
                                            >
                                                <option value="available">Available</option>
                                                <option value="unavailable">Unavailable</option>
                                                <option value="pending">Pending</option>
                                                <option value="rejected">Rejected</option>
                                            </select>
                                        </td>
                                        <td>
                                            {mosque.verified ? (
                                                <span className="verified-badge">Yes</span>
                                            ) : (
                                                <span className="unverified-badge">No</span>
                                            )}
                                        </td>
                                        <td>
                                            {mosque.linkedRestaurantID ? (
                                                <span className="has-restaurant-badge" title={`Linked: ${mosque.linkedRestaurantID}`}>
                                                    <Utensils size={14} /> Yes
                                                </span>
                                            ) : (
                                                <span className="no-restaurant">—</span>
                                            )}
                                        </td>
                                        <td>{mosque.totalVisits ?? 0}</td>
                                        <td title="Get directions clicked">
                                            <span className="direction-clicks">
                                                <Navigation size={14} />
                                                {mosque.directionClicks ?? 0}
                                            </span>
                                        </td>
                                        <td>
                                            {hasCoordinates(mosque) ? (
                                                <span className="coords-badge success" title={`${mosque.location.latitude.toFixed(6)}, ${mosque.location.longitude.toFixed(6)}`}>
                                                    ✓ Has Coords
                                                </span>
                                            ) : (
                                                <span className="coords-badge missing">Missing</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button
                                                    className="action-btn view"
                                                    onClick={() => navigate(`/mosques/${mosque.mosqueID}`, { state: { returnTo: returnToUrl } })}
                                                    title="View"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                <button
                                                    className="action-btn edit"
                                                    onClick={() => navigate(`/mosques/${mosque.mosqueID}/edit`, { state: { returnTo: returnToUrl } })}
                                                    title="Edit mosque"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                                {filters.year !== currentYear && (
                                                    <button
                                                        className="action-btn copy"
                                                        onClick={() => openCopyOverlay(mosque)}
                                                        disabled={copyingMosqueId === mosque.mosqueID}
                                                        title={`Add to ${currentYear} list`}
                                                    >
                                                        {copyingMosqueId === mosque.mosqueID ? (
                                                            <Loader size={16} className="spinning" />
                                                        ) : (
                                                            <CopyPlus size={16} />
                                                        )}
                                                    </button>
                                                )}
                                                {!hasCoordinates(mosque) && mosque.googleMapsLink && (
                                                    <button
                                                        className="action-btn extract"
                                                        onClick={() => handleExtractCoordinates(mosque.mosqueID)}
                                                        disabled={extractingCoordinates}
                                                        title="Extract Coordinates"
                                                    >
                                                        {extractingCoordinates ? (
                                                            <Loader size={16} className="spinning" />
                                                        ) : (
                                                            <MapPin size={16} />
                                                        )}
                                                    </button>
                                                )}
                                                <button
                                                    className="action-btn delete"
                                                    onClick={() => handleDelete(mosque.mosqueID)}
                                                    title="Delete"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {pagination.pages > 1 && (
                        <div className="pagination">
                            <button
                                disabled={filters.page === 1}
                                onClick={() => updateFilters({ page: filters.page - 1 })}
                            >
                                Previous
                            </button>
                            <span>
                                Page {pagination.page} of {pagination.pages}
                            </span>
                            <button
                                disabled={filters.page === pagination.pages}
                                onClick={() => updateFilters({ page: filters.page + 1 })}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {copyOverlayMosque && (
                <div className="copy-overlay" onClick={(e) => e.target === e.currentTarget && closeCopyOverlay()}>
                    <div className="copy-overlay-modal" onClick={e => e.stopPropagation()}>
                        <div className="copy-overlay-header">
                            <h3>Add to {currentYear} List</h3>
                            <button className="copy-overlay-close" onClick={closeCopyOverlay} aria-label="Close">
                                <X size={20} />
                            </button>
                        </div>
                        <p className="copy-overlay-subtitle">Update details before creating the copy:</p>
                        <div className="copy-overlay-form">
                            <div className="form-group">
                                <label>Name</label>
                                <input
                                    type="text"
                                    value={copyOverlayForm.name}
                                    onChange={(e) => setCopyOverlayForm(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Mosque name"
                                />
                            </div>
                            <div className="form-group">
                                <label>Food / Dish</label>
                                <select
                                    value={copyOverlayForm.dish}
                                    onChange={(e) => setCopyOverlayForm(prev => ({ ...prev, dish: e.target.value }))}
                                >
                                    {DISH_OPTIONS.map(d => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Description (optional)</label>
                                <textarea
                                    value={copyOverlayForm.description}
                                    onChange={(e) => setCopyOverlayForm(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Description"
                                    rows={3}
                                />
                            </div>
                        </div>
                        <div className="copy-overlay-actions">
                            <button onClick={closeCopyOverlay} disabled={copyingMosqueId}>Cancel</button>
                            <button
                                className="btn-primary"
                                onClick={handleCopyOverlaySubmit}
                                disabled={copyingMosqueId || !copyOverlayForm.name?.trim()}
                            >
                                {copyingMosqueId ? (
                                    <>
                                        <Loader size={16} className="spinning" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <CopyPlus size={16} />
                                        Create Copy
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {bulkProgress && (
                <div className="progress-overlay">
                    <div className="progress-modal">
                        <div className="progress-header">
                            <h3>
                                {bulkProgress.current === bulkProgress.total
                                    ? 'Coordinate Extraction Completed'
                                    : 'Extracting Coordinates...'}
                            </h3>
                            <button className="progress-close" onClick={() => setBulkProgress(null)} aria-label="Close progress">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="progress-content">
                            {bulkProgress.current === bulkProgress.total && (
                                <div className={`completion-message ${bulkProgress.errorCount > 0 ? 'warning' : 'success'}`}>
                                    {bulkProgress.errorCount > 0
                                        ? `Extraction completed with ${bulkProgress.successCount} success(es) and ${bulkProgress.errorCount} failure(s).`
                                        : `Successfully extracted coordinates for ${bulkProgress.successCount} mosque${bulkProgress.successCount > 1 ? 's' : ''}!`}
                                    {bulkProgress.skippedCount > 0 && (
                                        <div>{bulkProgress.skippedCount} mosque(s) skipped (already have coordinates).</div>
                                    )}
                                </div>
                            )}
                            <div className="progress-stats">
                                <div className="progress-stat">
                                    <span className="stat-label">Progress:</span>
                                    <span className="stat-value">
                                        {bulkProgress.current} / {bulkProgress.total}
                                    </span>
                                </div>
                                <div className="progress-stat">
                                    <span className="stat-label">Success:</span>
                                    <span className="stat-value success">{bulkProgress.successCount}</span>
                                </div>
                                <div className="progress-stat">
                                    <span className="stat-label">Failed:</span>
                                    <span className="stat-value error">{bulkProgress.errorCount}</span>
                                </div>
                            </div>
                            
                            <div className="progress-bar-container">
                                <div 
                                    className={`progress-bar ${bulkProgress.current === bulkProgress.total ? 'completed' : ''}`}
                                    style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                                />
                            </div>

                            {bulkProgress.errors && bulkProgress.errors.length > 0 && (
                                <div className="progress-errors">
                                    <strong>Errors:</strong>
                                    <div className="errors-list">
                                        {bulkProgress.errors.slice(0, 10).map((err, idx) => (
                                            <div key={idx} className="error-item">
                                                <span className="error-mosque">{err.mosqueID || 'Unknown'}:</span>
                                                <span className="error-message">{err.error}</span>
                                            </div>
                                        ))}
                                        {bulkProgress.errors.length > 10 && (
                                            <div className="error-item">... and {bulkProgress.errors.length - 10} more</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MosqueList;
